import { useCallback, useEffect, useState } from 'react';

import { DirectusFehler, lese } from './client';
import type { FehlerArt } from './client';
import {
  alsDokument,
  alsGlossareintrag,
  alsPhase,
  alsTermin,
  hatBrauchbaresDatum,
} from './einlesen';
import type { Inhalte } from './typen';

type Roh = Record<string, unknown>;

// Welche Felder geladen werden. Ausdruecklich aufgezaehlt statt "*", damit
// eine spaeter in Directus ergaenzte Spalte nicht unbemerkt mitkommt und die
// Antwort klein bleibt.
const FELDER_PHASEN = ['id', 'nummer', 'name', 'zeitraum', 'monate', 'schwerpunkt'];

const FELDER_TERMINE = [
  'id',
  'titel',
  'datum',
  'aktionstext',
  'phase.id',
  'phase.nummer',
  'phase.name',
  'dokument.id',
  'dokument.titel',
  'dokument.datei',
];

const FELDER_GLOSSAR = [
  'id',
  'begriff',
  'erklaerung',
  'rechtsgrundlage',
  'dokument.id',
  'dokument.titel',
  'dokument.datei',
];

const FELDER_DOKUMENTE = [
  'id',
  'titel',
  'datei',
  'kategorie',
  'version',
  'schnellzugriff',
  'phase.id',
  'phase.nummer',
  'phase.name',
];

export type LadeZustand =
  | { status: 'laedt' }
  | { status: 'fertig'; inhalte: Inhalte }
  | { status: 'fehler'; art: FehlerArt; technisch: string };

/**
 * Laedt alle vier Collections.
 *
 * Bewusst alles auf einmal und alles oder nichts: Die Datenmengen sind winzig,
 * und eine App, die den Jahresfahrplan zeigt, aber beim Glossar schweigt, ist
 * schwerer zu verstehen als eine, die ehrlich sagt, dass die Verbindung fehlt.
 */
export function useInhalte(): { zustand: LadeZustand; erneutLaden: () => void } {
  const [zustand, setZustand] = useState<LadeZustand>({ status: 'laedt' });
  const [versuch, setVersuch] = useState(0);

  const erneutLaden = useCallback(() => {
    setZustand({ status: 'laedt' });
    setVersuch((v) => v + 1);
  }, []);

  useEffect(() => {
    const abbruch = new AbortController();

    async function laden(): Promise<void> {
      try {
        const [phasenRoh, termineRoh, glossarRoh, dokumenteRoh] = await Promise.all([
          lese<Roh>('phasen', FELDER_PHASEN, { sort: 'nummer' }, abbruch.signal),
          lese<Roh>('termine', FELDER_TERMINE, { sort: 'datum' }, abbruch.signal),
          lese<Roh>('glossar', FELDER_GLOSSAR, { sort: 'begriff' }, abbruch.signal),
          lese<Roh>('dokumente', FELDER_DOKUMENTE, { sort: 'titel' }, abbruch.signal),
        ]);

        if (abbruch.signal.aborted) return;

        setZustand({
          status: 'fertig',
          inhalte: {
            phasen: phasenRoh.map(alsPhase),
            termine: termineRoh.map(alsTermin).filter(hatBrauchbaresDatum),
            glossar: glossarRoh.map(alsGlossareintrag),
            dokumente: dokumenteRoh.map(alsDokument),
          },
        });
      } catch (fehler) {
        if (abbruch.signal.aborted) return;

        if (fehler instanceof DirectusFehler) {
          setZustand({ status: 'fehler', art: fehler.art, technisch: fehler.technisch });
        } else {
          setZustand({
            status: 'fehler',
            art: 'antwort-unbrauchbar',
            technisch: fehler instanceof Error ? fehler.message : String(fehler),
          });
        }
      }
    }

    void laden();

    return () => {
      abbruch.abort();
    };
  }, [versuch]);

  return { zustand, erneutLaden };
}
